import { useRef, useState } from "react";
import { Upload, Loader2, Check } from "lucide-react";
import { api, errMsg, fileUrl } from "@/lib/api";
import { STATES, SERVICES, CERTIFICATIONS, GOALS, VOLUMES, CITIES } from "@/lib/constants";
import { toast } from "sonner";

export const Label = ({ children }) => <label className="block text-xs font-bold uppercase tracking-wider text-white/50 mb-2">{children}</label>;

export const Chips = ({ options, value, onChange, testid, render = (o) => o }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((o) => {
      const key = Array.isArray(o) ? o[0] : o;
      const on = value.includes(key);
      return (
        <button type="button" key={key} onClick={() => onChange(on ? value.filter((v) => v !== key) : [...value, key])}
          data-testid={`${testid}-${String(key).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          className={`text-xs font-semibold rounded-full px-3.5 py-2 border transition-colors ${on ? "bg-qi-pink border-qi-pink text-white" : "bg-white/[0.03] border-white/10 text-white/60 hover:border-white/30"}`}>
          {render(o)}
        </button>
      );
    })}
  </div>
);

export const Toggle = ({ on, onChange, label, testid }) => (
  <button type="button" onClick={() => onChange(!on)} data-testid={testid}
    className={`flex items-center gap-3 w-full text-left rounded-2xl px-4 py-3.5 border transition-colors ${on ? "border-qi-emerald/40 bg-qi-emerald/10" : "border-white/10 bg-white/[0.02]"}`}>
    <span className={`w-5 h-5 rounded-md flex items-center justify-center ${on ? "bg-qi-emerald" : "border border-white/30"}`}>{on && <Check size={13} className="text-ink" />}</span>
    <span className="text-sm font-semibold">{label}</span>
  </button>
);

const LogoUpload = ({ value, onChange }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/logo", fd);
      onChange(data.path);
    } catch (ex) {
      toast.error(errMsg(ex));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-5">
      <div className="w-24 h-24 rounded-2xl bg-white/5 border border-dashed border-white/20 flex items-center justify-center overflow-hidden" data-testid="intake-logo-preview">
        {value ? <img src={fileUrl(value)} alt="Logo" className="w-full h-full object-contain bg-white p-1.5" /> : <Upload className="text-white/30" />}
      </div>
      <div>
        <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={pick} data-testid="intake-logo-upload-input" />
        <button type="button" onClick={() => ref.current.click()} className="btn-ghost !py-2 !px-4 text-sm" data-testid="intake-logo-upload-button">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {value ? "Replace logo" : "Upload logo"}
        </button>
        <p className="text-xs text-white/40 mt-2">PNG, JPG or WEBP · max 5MB</p>
      </div>
    </div>
  );
};

export const StepBusiness = ({ f, set, setVal }) => (
  <div className="grid sm:grid-cols-2 gap-4">
    <div className="sm:col-span-2"><Label>Company logo</Label><LogoUpload value={f.logo_path} onChange={(v) => setVal("logo_path", v)} /></div>
    <div className="sm:col-span-2"><Label>Company name *</Label><input className="field" value={f.company_name} onChange={set("company_name")} data-testid="intake-company-input" /></div>
    <div><Label>Primary contact *</Label><input className="field" value={f.contact_name} onChange={set("contact_name")} data-testid="intake-contact-input" /></div>
    <div><Label>Title</Label><input className="field" value={f.title} onChange={set("title")} data-testid="intake-title-input" /></div>
    <div><Label>Business email *</Label><input type="email" className="field" value={f.business_email} onChange={set("business_email")} data-testid="intake-email-input" /></div>
    <div><Label>Phone</Label><input className="field" value={f.phone} onChange={set("phone")} data-testid="intake-phone-input" /></div>
    <div className="sm:col-span-2"><Label>Website</Label><input className="field" placeholder="www.yourfirm.com" value={f.website} onChange={set("website")} data-testid="intake-website-input" /></div>
    <div><Label>City</Label><input className="field" value={f.city} onChange={set("city")} data-testid="intake-city-input" /></div>
    <div><Label>Headquarters state *</Label>
      <select className="field" value={f.state} onChange={set("state")} data-testid="intake-state-select">
        <option value="" className="bg-ink">Select</option>
        {STATES.map(([c, n]) => <option key={c} value={c} className="bg-ink">{n}</option>)}
      </select>
    </div>
    <div className="sm:col-span-2"><Label>Brief business bio * ({f.bio.length}/1200)</Label>
      <textarea rows={5} maxLength={1200} className="field resize-none" placeholder="Who you are, how long you've been facilitating exchanges, and what makes your firm different." value={f.bio} onChange={set("bio")} data-testid="intake-bio-input" />
    </div>
  </div>
);

export const StepCoverage = ({ f, setVal }) => {
  const inScope = (code) => f.nationwide || code === f.state || f.states_served.includes(code);
  const metros = CITIES.filter(([, c]) => inScope(c)).map(([n, c]) => `${n}, ${c}`);
  return (
    <div className="space-y-6">
      <Toggle on={f.nationwide} onChange={(v) => setVal("nationwide", v)} label="We facilitate exchanges nationwide" testid="intake-nationwide-toggle" />
      {!f.nationwide && <div><Label>States served</Label><Chips options={STATES} value={f.states_served} onChange={(v) => setVal("states_served", v)} testid="intake-state-chip" render={(o) => o[0]} /></div>}
      <div>
        <Label>Metros you actively serve</Label>
        <p className="text-xs text-white/40 -mt-1 mb-3">You'll be featured as a local firm on these city pages. Based on your states above.</p>
        {metros.length ? <Chips options={metros} value={f.metros_served} onChange={(v) => setVal("metros_served", v)} testid="intake-metro-chip" />
          : <p className="text-sm text-white/40" data-testid="intake-metro-empty">No featured metros in your selected states yet.</p>}
      </div>
      <div><Label>Exchange types offered *</Label><Chips options={SERVICES} value={f.services} onChange={(v) => setVal("services", v)} testid="intake-service-chip" /></div>
    </div>
  );
};

export const StepCredentials = ({ f, set, setVal }) => (
  <div className="space-y-5">
    <div className="grid sm:grid-cols-2 gap-3">
      <Toggle on={f.fea_member} onChange={(v) => setVal("fea_member", v)} label="FEA member" testid="intake-fea-toggle" />
      <Toggle on={f.fidelity_bond} onChange={(v) => setVal("fidelity_bond", v)} label="Fidelity bond in place" testid="intake-bond-toggle" />
      <Toggle on={f.eo_insurance} onChange={(v) => setVal("eo_insurance", v)} label="Errors & omissions insurance" testid="intake-eo-toggle" />
      <Toggle on={f.segregated_accounts} onChange={(v) => setVal("segregated_accounts", v)} label="Segregated / qualified escrow accounts" testid="intake-segregated-toggle" />
    </div>
    {f.fidelity_bond && <div><Label>Bond amount</Label><input className="field" placeholder="e.g. $10,000,000" value={f.bond_amount} onChange={set("bond_amount")} data-testid="intake-bond-amount-input" /></div>}
    <div><Label>Certifications & licenses</Label><Chips options={CERTIFICATIONS} value={f.certifications} onChange={(v) => setVal("certifications", v)} testid="intake-cert-chip" /></div>
    <div className="grid sm:grid-cols-2 gap-4">
      <div><Label>Years in business</Label><input type="number" min={0} max={100} className="field" value={f.years_in_business} onChange={set("years_in_business")} data-testid="intake-years-input" /></div>
      <div><Label>Exchanges per year</Label>
        <select className="field" value={f.exchanges_per_year} onChange={set("exchanges_per_year")} data-testid="intake-volume-select">
          <option value="" className="bg-ink">Select</option>
          {VOLUMES.map((v) => <option key={v} value={v} className="bg-ink">{v}</option>)}
        </select>
      </div>
    </div>
  </div>
);

export const StepGoals = ({ f, set, setVal }) => (
  <div className="space-y-5">
    <div><Label>What are your business goals for the next 12 months?</Label><Chips options={GOALS} value={f.business_goals} onChange={(v) => setVal("business_goals", v)} testid="intake-goal-chip" /></div>
    <div><Label>Tell us more about your goals</Label><textarea rows={4} className="field resize-none" value={f.goals_notes} onChange={set("goals_notes")} data-testid="intake-goals-notes-input" /></div>
    <div><Label>Describe your ideal investor client</Label><textarea rows={3} className="field resize-none" placeholder="Property types, deal sizes, regions..." value={f.ideal_client} onChange={set("ideal_client")} data-testid="intake-ideal-client-input" /></div>
    <div><Label>How did you hear about us?</Label><input className="field" value={f.referral_source} onChange={set("referral_source")} data-testid="intake-referral-input" /></div>
    <p className="text-xs text-white/40">Goals and ideal-client details are private — only you and the site admin see them.</p>
  </div>
);
